import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDate } from "@/lib/utils/date";
import { DocumentTable } from "../components/document-table/document-table";
import type { DocumentData, LotData } from "../components/document-table/types";
import { PdfViewer, type PdfViewerFile } from "../components/pdf-viewer";

type DoZoneSummary = {
  id: number;
  name: string;
  shortName: string | null;
  type: "horizontale" | "verticale";
  interventionCount: number;
  withoutDocumentCount: number;
  viseIntegralementCount: number;
  viseFavorableCount: number;
  diffusedDocumentCount: number;
};
type BuildingSummary = {
  id: number;
  name: string;
  shortName: string | null;
  zones: DoZoneSummary[];
};
type FilterOption = { id: number; name: string };
type SummaryData = {
  filters: {
    lots: FilterOption[];
    performances: Array<FilterOption & { lotId: number }>;
  };
  buildings: BuildingSummary[];
};
type RangOneContract = { id: number; label: string; companyName: string };
type DoIntervention = {
  id: number;
  dateIntervention: string | null;
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  rangOneContracts: RangOneContract[];
  documents: DocumentData[];
};
type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: SummaryData };
type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; interventions: DoIntervention[] };

const parseIds = (value: string | null) =>
  value
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0) ?? [];

export function AvancementEtudesDo() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null,
  );
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const [pdfFile, setPdfFile] = useState<PdfViewerFile | null>(null);
  const lotQuery = searchParams.get("lot") ?? "";
  const performanceQuery = searchParams.get("performance") ?? "";
  const lots = parseIds(lotQuery);
  const performances = parseIds(performanceQuery);
  const recentOnly = searchParams.get("recentOnly") === "true";

  useEffect(() => {
    if (isNil(yardId)) return;
    let cancelled = false;
    setState({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/do", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        setSelectedBuildingId(
          (current) => current ?? data.data.buildings[0]?.id ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, lotQuery, performanceQuery]);

  useEffect(() => {
    if (isNil(yardId) || isNil(selectedZoneId)) {
      setDetail({ status: "idle" });
      return;
    }
    let cancelled = false;
    setDetail({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/do/zones/{zoneId}", {
        params: {
          path: { yardId, zoneId: String(selectedZoneId) },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            recentOnly: recentOnly ? "true" : "false",
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setDetail({ status: "error" });
          return;
        }
        setDetail({
          status: "success",
          interventions: data.data.interventions,
        });
      })
      .catch(() => {
        if (!cancelled) setDetail({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, selectedZoneId, lotQuery, performanceQuery, recentOnly]);

  const updateFilter = (key: "lot" | "performance", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };
  const setRecentOnly = (checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set("recentOnly", "true");
    else next.delete("recentOnly");
    setSearchParams(next, { replace: true });
  };

  const viewPdf = useCallback(
    async (fileId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fileId) } } },
      );
      if (isNotNil(data)) setPdfFile(data.data);
    },
    [yardId],
  );
  const downloadNative = useCallback(
    async (fileId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fileId) } } },
      );
      if (isNotNil(data))
        window.open(data.data.url, "_blank", "noopener,noreferrer");
    },
    [yardId],
  );

  if (state.status === "loading")
    return <PageMessage text="Chargement de l'avancement des études…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger l'avancement des études."
        error
      />
    );
  if (state.data.buildings.length === 0)
    return (
      <PageMessage text="Aucun bâtiment n'est rattaché à votre contrat sur ce chantier." />
    );

  const selectedBuilding =
    state.data.buildings.find((item) => item.id === selectedBuildingId) ??
    state.data.buildings[0];
  const selectedZone =
    selectedBuilding?.zones.find((item) => item.id === selectedZoneId) ?? null;

  return (
    <div className="flex h-full flex-col gap-5 overflow-hidden p-4 lg:p-6">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Avancement des études
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Visualisez l'avancement des études de vos bâtiments par zone.
        </p>
      </header>
      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-56">
          <Select
            label="Lots"
            multiple
            options={state.data.filters.lots.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={lots.map(String)}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Prestations"
            multiple
            options={state.data.filters.performances.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={performances.map(String)}
            onValueChange={({ value }) => updateFilter("performance", value)}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={(event) => setRecentOnly(event.target.checked)}
          />
          Indices récents uniquement
        </label>
      </section>
      <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto lg:grid-cols-[240px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden">
        <nav aria-label="Bâtiments" className="space-y-1 lg:overflow-y-auto">
          {state.data.buildings.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setSelectedBuildingId(item.id);
                setSelectedZoneId(null);
              }}
              className={cn(
                "w-full rounded-lg px-3 py-2 text-left text-sm",
                item.id === selectedBuilding?.id
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-100",
              )}
            >
              {item.shortName ?? item.name}
            </button>
          ))}
        </nav>
        <main className="min-w-0 space-y-3 lg:overflow-y-auto">
          <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
            {selectedBuilding?.name}
          </h3>
          {selectedBuilding?.zones.length === 0 && (
            <p className="text-sm text-zinc-500">
              Aucune zone dans ce bâtiment.
            </p>
          )}
          {selectedBuilding?.zones.map((zoneItem) => (
            <DoZoneCard
              key={zoneItem.id}
              zone={zoneItem}
              open={zoneItem.id === selectedZoneId}
              onToggle={() =>
                setSelectedZoneId(
                  zoneItem.id === selectedZoneId ? null : zoneItem.id,
                )
              }
            />
          ))}
          {isNotNil(selectedZone) && (
            <DoZoneDetail
              state={detail}
              onViewPdf={viewPdf}
              onDownloadNative={downloadNative}
            />
          )}
        </main>
      </div>
      {isNotNil(pdfFile) && (
        <PdfViewer file={pdfFile} onClose={() => setPdfFile(null)} />
      )}
    </div>
  );
}

function PageMessage({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "p-8 text-center text-sm",
        error ? "text-red-600" : "text-zinc-500",
      )}
    >
      {text}
    </div>
  );
}

function DoZoneCard({
  zone,
  open,
  onToggle,
}: {
  zone: DoZoneSummary;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="w-full rounded-lg border border-zinc-200 bg-white p-4 text-left dark:border-zinc-700 dark:bg-zinc-900"
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-zinc-900 dark:text-zinc-100">
          {zone.name}
        </span>
        {open ? <CaretDownIcon /> : <CaretRightIcon />}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-zinc-600 md:grid-cols-3 dark:text-zinc-400">
        <span>{zone.interventionCount} intervention(s)</span>
        <span>{zone.withoutDocumentCount} sans document</span>
        <span>{zone.diffusedDocumentCount} document(s) diffusé(s)</span>
        <span>
          {zone.viseIntegralementCount} visé(s) intégralement (dernier indice
          reçu)
        </span>
        <span>
          {zone.viseFavorableCount} visé(s) intégralement favorablement
        </span>
      </div>
    </button>
  );
}

type DoZoneDetailProps = {
  state: DetailState;
  onViewPdf: (id: number) => void;
  onDownloadNative: (id: number) => void;
};
function DoZoneDetail({
  state,
  onViewPdf,
  onDownloadNative,
}: DoZoneDetailProps) {
  if (state.status === "idle" || state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger les interventions de cette zone."
        error
      />
    );
  if (state.interventions.length === 0)
    return (
      <PageMessage text="Aucune intervention ne correspond aux filtres." />
    );
  return (
    <div className="space-y-4">
      {state.interventions.map((item) => {
        const lots: LotData[] = [
          {
            id: item.lot.id,
            name: item.lot.name,
            nLot: item.lot.nLot,
            prestas: [
              {
                id: item.performance.id,
                name: item.performance.name,
                documents: item.documents,
              },
            ],
          },
        ];
        return (
          <article
            key={item.id}
            className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="font-semibold text-zinc-900 dark:text-zinc-100">
                  {item.lot.nLot ? `Lot ${item.lot.nLot} — ` : ""}
                  {item.performance.name}
                </h4>
                <p className="mt-1 text-sm text-zinc-500">
                  {item.rangOneContracts.length === 0
                    ? "Aucun contrat travaux rang 1 actif"
                    : item.rangOneContracts
                        .map(
                          (contract) =>
                            `${contract.label} (${contract.companyName})`,
                        )
                        .join(" · ")}
                </p>
                {item.rangOneContracts.length > 1 && (
                  <p className="mt-1 text-xs text-amber-700">
                    Plusieurs contrats travaux rang 1 actifs
                  </p>
                )}
              </div>
              <p className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
                Date de démarrage presta
                <span className="mt-1 block text-sm font-normal">
                  {isNotNil(item.dateIntervention)
                    ? formatDate(item.dateIntervention)
                    : "—"}
                </span>
              </p>
            </div>
            <div className="mt-4">
              {item.documents.length === 0 ? (
                <p className="rounded-lg bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500 dark:bg-zinc-800/50">
                  Aucun document produit pour cette intervention.
                </p>
              ) : (
                <DocumentTable
                  lots={lots}
                  canMarkBpe={false}
                  onViewPdf={onViewPdf}
                  onDownloadNative={onDownloadNative}
                  onMarkBpe={() => undefined}
                  onOpenSynthese={() => undefined}
                  onOpenDestinataires={() => undefined}
                  onOpenDiffusion={() => undefined}
                  onOpenInterne={() => undefined}
                  showDiffusionActions={false}
                />
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
